import React, { useState, useRef, useEffect } from 'react';

const TongueTwisterChallengeGame = ({ gameData, onComplete }) => {
    const [currentTwisterIndex, setCurrentTwisterIndex] = useState(0);
    const [completedTwisters, setCompletedTwisters] = useState([]);
    const [score, setScore] = useState(0);
    const [isRecording, setIsRecording] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    useEffect(() => {
        return () => {
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    }, [audioUrl]);

    if (!gameData || !gameData.tongue_twisters) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-indigo-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🎙️</div>
                <h3 className="text-base md:text-3xl font-black text-indigo-900 tracking-tighter uppercase">Ruošiamas mikrofonas...</h3>
            </div>
        );
    }

    const currentTwister = gameData.tongue_twisters[currentTwisterIndex];
    const totalTwisters = gameData.tongue_twisters.length;
    const pointsPerTwister = gameData.points_per_twister || 20;

    const speak = (text) => {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'lt-LT';
        utterance.rate = 0.85;
        window.speechSynthesis.speak(utterance);
        if (navigator.vibrate) navigator.vibrate(30);
    };

    const handleSkip = () => {
        if (currentTwisterIndex < totalTwisters - 1) {
            setCurrentTwisterIndex(prev => prev + 1);
            setShowHint(false);
            setAudioUrl(null);
            setAudioBlob(null);
            if (navigator.vibrate) navigator.vibrate(40);
        }
    };

    const handlePrevious = () => {
        if (currentTwisterIndex > 0) {
            setCurrentTwisterIndex(prev => prev - 1);
            setShowHint(false);
            setAudioUrl(null);
            setAudioBlob(null);
            if (navigator.vibrate) navigator.vibrate(40);
        }
    };

    const handleComplete = () => {
        if (feedback) return;
        setFeedback('correct');
        const pointsToAdd = pointsPerTwister;
        
        if (!completedTwisters.includes(currentTwister.id)) {
            setCompletedTwisters(prev => [...prev, currentTwister.id]);
            setScore(prev => prev + pointsToAdd);
        }
        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

        setTimeout(() => {
            setFeedback(null);
            setAudioUrl(null);
            setAudioBlob(null);
            if (currentTwisterIndex < totalTwisters - 1) {
                setCurrentTwisterIndex(prev => prev + 1);
                setShowHint(false);
                if (navigator.vibrate) navigator.vibrate(30);
            } else {
                if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
                onComplete?.(score + pointsToAdd);
            }
        }, 3000);
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
                const url = URL.createObjectURL(blob);
                setAudioBlob(blob);
                setAudioUrl(url);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setAudioUrl(null);
            setAudioBlob(null);
            if (navigator.vibrate) navigator.vibrate(60);
        } catch (err) {
            console.error("Microphone access denied:", err);
            alert("Leiskite naudoti mikrofoną!");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (navigator.vibrate) navigator.vibrate(40);
        }
    };

    const playBack = () => {
        if (audioUrl) {
            const audio = new Audio(audioUrl);
            audio.play();
            if (navigator.vibrate) navigator.vibrate(20);
        }
    };

    const translateDifficulty = (diff) => {
        const translations = {
            'easy': 'Lengvas', 'medium': 'Vidutinis', 'hard': 'Sunkus',
            'EASY': 'Lengvas', 'MEDIUM': 'Vidutinis', 'HARD': 'Sunkus'
        };
        return translations[diff] || diff;
    };

    const progress = (completedTwisters.length / totalTwisters) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Vocal HUD */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-indigo-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-2 md:p-6">
                        <div className="w-20 h-20 bg-indigo-100 rounded-3xl flex items-center justify-center text-lg md:text-4xl shadow-inner border-2 border-indigo-200 animate-float">
                            🎙️
                        </div>
                        <div>
                            <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                                Žodžių <span className="text-indigo-600">Laužikliai</span>
                            </h3>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="bg-indigo-600 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase shadow-lg">
                                    {translateDifficulty(gameData.difficulty)}
                                </span>
                                <p className="text-slate-500 font-bold italic font-serif">
                                    Ištark šią frazę greitai ir be klaidų!
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 md:p-6">
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Talentas</p>
                            <p className="text-base md:text-3xl font-black text-indigo-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[120px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Iššūkiai</p>
                            <p className="text-base md:text-3xl font-black text-slate-800 leading-none">{completedTwisters.length}/{totalTwisters}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 w-full bg-slate-200/50 rounded-full h-4 p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-indigo-400 via-purple-500 to-indigo-700 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(99,102,241,0.4)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Main Vocal Interaction Stage */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-20 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center justify-center min-h-[50vh] text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-[250px] opacity-[0.03] select-none pointer-events-none rotate-12">📻</div>
                <div className="absolute bottom-0 left-0 p-4 md:p-12 text-[250px] opacity-[0.03] select-none pointer-events-none -rotate-12 text-indigo-600">🎙️</div>
                
                <div className="w-full max-w-5xl space-y-16 relative z-10">
                    <div className="space-y-4 md:space-y-10">
                        <div className="text-2xl md:text-6xl md:text-[180px] drop-shadow-[0_40px_80px_rgba(99,102,241,0.3)] animate-float mb-8">{currentTwister.emoji}</div>
                        
                        <div className="bg-indigo-600 text-white px-10 py-4 rounded-lg md:rounded-[2.5rem] font-black text-2xl shadow-[0_20px_40px_rgba(79,70,229,0.3)] inline-flex items-center gap-2 md:p-6 animate-pop-in border-4 border-indigo-400">
                            <span className="text-[12px] opacity-60 uppercase tracking-[0.3em]">Tikslinis Garsas:</span>
                            <span className="text-xl md:text-5xl font-serif italic drop-shadow-md">{currentTwister.target_sound}</span>
                        </div>
                    </div>

                    {/* Futuristic Twister Board */}
                    <div className="w-full bg-white rounded-[4.5rem] p-4 md:p-12 lg:p-24 shadow-2xl border-[12px] border-slate-900 relative group overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(79,70,229,0.03)_0%,transparent_100%)] pointer-events-none" />
                        <button 
                            onClick={() => speak(currentTwister.text)}
                            className="absolute -top-4 md:p-10 -right-10 w-28 h-28 bg-indigo-600 text-white rounded-full shadow-2xl border-8 border-white flex items-center justify-center text-xl md:text-5xl hover:scale-110 active:scale-95 transition-all group z-20"
                        >
                            <span className="group-hover:animate-pulse">🔊</span>
                        </button>
                        <p className="text-lg md:text-4xl md:text-7xl lg:text-8xl font-black text-slate-950 text-center leading-[1.1] tracking-tighter italic font-serif drop-shadow-sm px-4">
                            {currentTwister.text}
                        </p>
                    </div>

                    {/* Recording Lab Controls */}
                    <div className="flex flex-col items-center gap-4 md:p-12 w-full max-w-3xl mx-auto">
                        {!audioUrl && !isRecording ? (
                            <button
                                onClick={startRecording}
                                disabled={!!feedback}
                                className="premium-button-3d group w-full py-12 bg-gradient-to-r from-rose-500 via-red-500 to-rose-700 text-white rounded-[3.5rem] font-black text-lg md:text-4xl shadow-[0_30px_60px_rgba(244,63,94,0.3)] flex items-center justify-center gap-3 md:p-8 transition-all border-b-[12px] border-rose-900 uppercase tracking-[0.2em]"
                            >
                                <span className="text-xl md:text-5xl animate-pulse group-hover:scale-125 transition-transform">🎤</span>
                                <span>PRADĖTI ĮRAŠYMĄ</span>
                            </button>
                        ) : isRecording ? (
                            <button
                                onClick={stopRecording}
                                className="w-full py-12 bg-slate-950 text-white rounded-2xl md:rounded-[4rem] font-black text-lg md:text-4xl shadow-2xl flex items-center justify-center gap-3 md:p-8 border-[12px] border-rose-500 animate-pulse relative overflow-hidden"
                            >
                                <div className="absolute inset-0 bg-rose-500/10 animate-pulse" />
                                <div className="w-10 h-10 bg-rose-500 rounded-lg animate-ping relative z-10" />
                                <span className="relative z-10 tracking-[0.1em]">SUSTABDYTI DABAR</span>
                            </button>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:p-8 w-full animate-pop-in">
                                <button 
                                    onClick={playBack} 
                                    className="premium-button-3d bg-white text-slate-900 p-4 md:p-10 rounded-xl md:rounded-[3rem] font-black text-2xl shadow-xl flex flex-col items-center gap-4 border-b-[12px] border-slate-200 active:translate-y-2 active:border-b-0"
                                >
                                    <span className="text-xl md:text-5xl">🎧</span>
                                    <span className="uppercase tracking-widest text-sm">Klausyti</span>
                                </button>
                                <button 
                                    onClick={startRecording} 
                                    className="premium-button-3d bg-slate-900 text-white p-4 md:p-10 rounded-xl md:rounded-[3rem] font-black text-2xl shadow-xl flex flex-col items-center gap-4 border-b-[12px] border-slate-950 active:translate-y-2 active:border-b-0"
                                >
                                    <span className="text-xl md:text-5xl text-rose-500">🔄</span>
                                    <span className="uppercase tracking-widest text-sm">Naujas</span>
                                </button>
                                <button 
                                    onClick={handleComplete} 
                                    className="premium-button-3d bg-emerald-500 text-white p-4 md:p-10 rounded-xl md:rounded-[3rem] font-black text-2xl shadow-xl flex flex-col items-center gap-4 border-b-[12px] border-emerald-800 active:translate-y-2 active:border-b-0"
                                >
                                    <span className="text-xl md:text-5xl">🎯</span>
                                    <span className="uppercase tracking-widest text-sm">Pavyko!</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Navigation Dashboard */}
                    <div className="w-full mt-10 pt-10 border-t-4 border-slate-100 flex flex-wrap justify-center gap-3 md:p-8">
                        <button
                            onClick={handlePrevious}
                            disabled={currentTwisterIndex === 0 || !!feedback || isRecording}
                            className="px-10 py-5 bg-white text-slate-400 rounded-3xl font-black text-sm uppercase tracking-[0.3em] shadow-xl disabled:opacity-10 border-b-4 border-slate-100 active:translate-y-1 active:border-b-0 transition-all"
                        >
                            ← Ankstesnis
                        </button>
                        <button
                            onClick={() => { if (navigator.vibrate) navigator.vibrate(30); setShowHint(!showHint); }}
                            className={`px-12 py-5 rounded-3xl font-black text-sm uppercase tracking-[0.3em] transition-all shadow-xl border-b-8 ${showHint ? 'bg-amber-500 text-white border-amber-700' : 'bg-white text-amber-600 border-amber-100 hover:bg-amber-50'}`}
                        >
                            💡 Pagalba
                        </button>
                        <button
                            onClick={handleSkip}
                            disabled={currentTwisterIndex === totalTwisters - 1 || !!feedback || isRecording}
                            className="px-10 py-5 bg-white text-slate-400 rounded-3xl font-black text-sm uppercase tracking-[0.3em] shadow-xl disabled:opacity-10 border-b-4 border-slate-100 active:translate-y-1 active:border-b-0 transition-all"
                        >
                            Kitas →
                        </button>
                    </div>

                    {showHint && (
                        <div className="mt-8 p-4 md:p-12 bg-amber-50 rounded-2xl md:rounded-[4rem] text-slate-800 text-base md:text-3xl font-bold italic shadow-inner border-4 border-amber-100 animate-pop-in max-w-4xl mx-auto text-center font-serif leading-relaxed tracking-tight relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-2 md:p-6 opacity-5 text-8xl font-black select-none text-amber-500 rotate-12">💡</div>
                            "{currentTwister.hint}"
                        </div>
                    )}
                </div>
            </div>

            {/* Immersive Vocal Feedback Overlay */}
            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-3xl animate-fade-in" />
                    <div className={`
                        max-w-3xl w-full p-4 md:p-20 rounded-3xl md:rounded-[5rem] bg-white shadow-[0_60px_120px_rgba(0,0,0,0.6)] relative animate-pop-in border-[16px] text-center
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-48 h-48 bg-white rounded-full flex items-center justify-center text-xl md:text-5xl md:text-[120px] shadow-2xl border-8 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🏆' : '🔇'}
                        </div>
                        <div className="space-y-4 md:space-y-12 pt-14">
                            <h2 className={`text-7xl font-black uppercase tracking-tighter leading-none italic font-serif ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'TIKRAS TALENTAS!' : 'PABANDYK VĖL!'}
                            </h2>
                            <div className={`p-4 md:p-12 rounded-2xl md:rounded-[4rem] border-4 text-center italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-lg md:text-4xl font-bold text-slate-800 leading-relaxed italic tracking-tight px-6">
                                    {feedback === 'correct' 
                                        ? 'Oho! Tavo dikcija yra nepriekaištinga. Ištarei šį liežuvio laužiklį kaip tikras oratorius!' 
                                        : 'Panašu, kad liežuvis šiek tiek susipynė. Nieko tokio, įrašyk dar kartą ir nugalėk šį iššūkį!'}
                                </p>
                            </div>
                            <div className="flex items-center justify-center gap-2 md:p-6">
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TongueTwisterChallengeGame;
